/**
 * Composant réutilisable pour afficher les identifiants LogiConnect
 * 
 * Affiche l'identifiant LogiConnect avec un style mis en évidence
 * Permet la copie au clic avec notification
 */
import React, { useState } from 'react';
import { Copy, Check, Hash } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';

const LogiConnectIdBadge = ({ 
  id, 
  label = "ID LogiConnect",
  showLabel = true,
  showIcon = true,
  variant = "default",
  className,
  onCopy,
  copyable = true,
  size = "default" // "default" | "sm" | "lg"
}) => {
  const [copied, setCopied] = useState(false);

  if (!id) {
    return null;
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      
      toast({
        title: "Copié !",
        description: "L'identifiant LogiConnect a été copié dans le presse-papiers",
      });

      if (onCopy) {
        onCopy(id);
      }

      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
      toast({
        title: "Erreur",
        description: "Impossible de copier l'identifiant",
        variant: "destructive",
      });
    }
  };

  const sizeClasses = {
    sm: "text-xs px-2 py-0.5",
    default: "text-sm px-3 py-1",
    lg: "text-base px-4 py-1.5"
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {showLabel && (
        <span className={cn(
          "text-muted-foreground font-medium",
          size === "sm" ? "text-xs" : size === "lg" ? "text-base" : "text-sm"
        )}>
          {label}:
        </span>
      )}
      <div className="flex items-center gap-1">
        <Badge
          variant={variant}
          className={cn(
            "font-mono font-semibold bg-gradient-to-r from-blue-500 to-teal-500 text-white border-0 shadow-sm",
            sizeClasses[size],
            copyable && "cursor-pointer hover:shadow-md transition-shadow"
          )}
          onClick={copyable ? handleCopy : undefined}
        >
          {showIcon && <Hash className={cn("mr-1", size === "sm" ? "w-3 h-3" : size === "lg" ? "w-5 h-5" : "w-4 h-4")} />}
          {id}
        </Badge>
        {copyable && (
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-auto w-auto p-1 hover:bg-transparent",
              size === "sm" && "h-4 w-4",
              size === "lg" && "h-6 w-6"
            )}
            onClick={handleCopy}
            title="Copier l'identifiant"
          >
            {copied ? (
              <Check className={cn(
                "text-green-500",
                size === "sm" ? "w-3 h-3" : size === "lg" ? "w-5 h-5" : "w-4 h-4"
              )} />
            ) : (
              <Copy className={cn(
                "text-muted-foreground hover:text-foreground",
                size === "sm" ? "w-3 h-3" : size === "lg" ? "w-5 h-5" : "w-4 h-4"
              )} />
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * Variante simple pour affichage inline dans du texte
 */
export const LogiConnectIdInline = ({ 
  id, 
  className,
  copyable = true 
}) => {
  const [copied, setCopied] = useState(false);

  if (!id) {
    return null;
  }

  const handleCopy = async () => {
    if (!copyable) return;
    
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      
      toast({
        title: "Copié !",
        description: "L'identifiant LogiConnect a été copié",
      });

      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };

  return (
    <span
      className={cn(
        "font-mono font-semibold text-blue-600 dark:text-blue-400",
        copyable && "cursor-pointer hover:underline",
        className
      )}
      onClick={copyable ? handleCopy : undefined}
      title={copyable ? "Cliquer pour copier" : ""}
    >
      {id}
    </span>
  );
};

export default LogiConnectIdBadge;

